/**
 * Lobby — pre-game seating screen. Owner: Phase-3 A3.3.
 *
 * Surface:
 *   - Header: room code + ShareLink (PRD FR-L1).
 *   - Mode controls (host only): 4p/6p toggle, jokers on/off, manual/random
 *     teams (PRD FR-L3). 6p-jokers-OFF is allowed (RULES §2; removes the 2s).
 *   - Seat list (one LobbySeat each): humans vs bots, "bot playing for X"
 *     badge for covered seats (PRD FR-B3), "(you)" / "(host)" markers
 *     (RULES §1.3, server picks host = lowest-seat connected human, ARCH §9.1).
 *   - Start button (host only). The server enforces lobby validity; on the
 *     client we enable it once `seats.length === config.players` (always true
 *     because the server resizes seats on `setConfig`, but the explicit gate
 *     keeps the intent visible per PRD FR-L5).
 *   - Reconnection hint: when `useConnection().kind === 'reconnecting'`,
 *     replace ShareLink with a soft "Reconnecting..." block (ARCH §5; the
 *     reclaim token is never surfaced — it stays in localStorage, ARCH §9.1).
 *   - Closed state: show a "Disconnected — refresh to retry" panel + Back
 *     button (calls `actions.disconnect()` and reloads).
 *
 * Seat-swap decision (recorded here for future agents):
 *   The server's `handleAssignSeat` (apps/server/src/room.ts) keys the mover
 *   on `seats.find(s => s.connId === msg.playerId)`. The client never learns
 *   its own `connId` — that field is server-internal and absent from the wire
 *   protocol (intentional anti-cheat / privacy by construction). Without
 *   modifying the protocol or the server (BOTH out of scope per the A3.3
 *   ownership boundary), there is no `playerId` value the client can supply
 *   that lets a host swap two arbitrary seats. The only reachable subset
 *   would be the host swapping THEMSELVES with another seat, but even that
 *   requires the host to know their own `connId`. So v1 ships with NO seat
 *   swap UI. Random teaming (the auction-shuffle on Start with teamMode =
 *   'random', RULES §1.3) covers the team-mix use case; manual mode keeps
 *   players in join order. The `canSwap` prop on LobbySeat is wired and
 *   `false`, so the affordance is forward-compatible the moment the server
 *   gains a swap path that doesn't depend on a client-side connId.
 *
 * Hooks: useLobby, useYou, useRoomCode, useConnection.
 * Actions: actions.setConfig, actions.start, actions.disconnect.
 *
 * RULES §1.3 (lobby teams), §3.1 (caller rotation); PRD §4 (flows),
 * §5.1 (FR-L1..FR-L6), §5.3 (FR-B3), §5.4 (reconnection).
 */
import { useLobby, useRoomCode, useYou, useConnection } from "../state/hooks.js";
import { actions } from "../wire/index.js";
import { LobbySeat } from "./LobbySeat.js";
import { ShareLink } from "./ShareLink.js";

function reloadPage(): void {
  if (typeof window !== "undefined") {
    try {
      window.location.reload();
    } catch {
      /* jsdom / SSR */
    }
  }
}

export function Lobby(): JSX.Element {
  const lobby = useLobby();
  const code = useRoomCode();
  const you = useYou();
  const conn = useConnection();

  // Loading: we have a code (in the URL/store) but the first `lobby` push
  // hasn't landed. The ConnectionBanner already shows transit state; this
  // keeps the screen from flashing empty between `connect()` and the server's
  // initial lobby broadcast. Note: the closed-state branch is handled globally
  // by App.tsx (Phase-3 F#7 hoist); reaching Lobby implies conn.kind !== "closed".
  if (!lobby) {
    return (
      <main className="lobby lobby-loading" aria-busy="true" aria-labelledby="lobby-loading-title">
        <h1 id="lobby-loading-title" className="lobby-title">Hookum</h1>
        <p className="lobby-loading-msg">Joining room {code ?? ""}…</p>
      </main>
    );
  }

  const hostSeatIndex = lobby.hostId === "" ? null : Number(lobby.hostId);
  const isHost = hostSeatIndex !== null && you !== null && hostSeatIndex === you;
  const hostSeat = hostSeatIndex !== null ? lobby.seats[hostSeatIndex] : undefined;
  const hostName = hostSeat?.displayName ?? "—";

  const canStart =
    isHost && lobby.seats.length === lobby.config.players && lobby.phase === "lobby";
  const reconnecting = conn.kind === "reconnecting";

  function onPlayersChange(players: 4 | 6): void {
    if (!isHost) return;
    // v1.2 Phase 13 (RULES §2 + §9 A3): 6-player is jokers-ON only. When the
    // host flips to 6 players, force jokers=true so the protocol's
    // GameConfigSchema.refine() doesn't reject the setConfig payload.
    const jokers = players === 6 ? true : lobby!.config.jokers;
    actions.setConfig(players, jokers, lobby!.config.teamMode);
  }
  function onJokersChange(jokers: boolean): void {
    if (!isHost) return;
    // v1.2 Phase 13: same invariant. The Jokers-OFF radio is `disabled` when
    // players===6 (see the fieldset below), so this branch is only hit if
    // some client bypasses the UI. We swallow the off-switch defensively.
    if (lobby!.config.players === 6 && !jokers) return;
    actions.setConfig(lobby!.config.players, jokers, lobby!.config.teamMode);
  }
  function onTeamModeChange(mode: "manual" | "random"): void {
    if (!isHost) return;
    actions.setConfig(lobby!.config.players, lobby!.config.jokers, mode);
  }
  function onStart(): void {
    if (!canStart) return;
    actions.start();
  }
  function onBack(): void {
    actions.disconnect();
    reloadPage();
  }

  return (
    <main className="lobby" aria-labelledby="lobby-title">
      <header className="lobby-header">
        <h1 id="lobby-title" className="lobby-title">Hookum</h1>
        {!isHost && hostSeatIndex !== null ? (
          <p className="lobby-host-line">
            <strong>{hostName}</strong> is the host
          </p>
        ) : null}
        {reconnecting ? (
          <div className="lobby-reconnecting" role="status" aria-live="polite">
            Reconnecting…
          </div>
        ) : (
          <ShareLink code={lobby.code} />
        )}
      </header>

      <section className="lobby-config" aria-labelledby="lobby-config-title">
        <h2 id="lobby-config-title" className="lobby-section-title">Game</h2>
        <div className="lobby-config-row">
          <fieldset className="lobby-fieldset" disabled={!isHost}>
            <legend>Players</legend>
            <label className="lobby-radio">
              <input
                type="radio"
                name="players"
                value="4"
                checked={lobby.config.players === 4}
                onChange={() => onPlayersChange(4)}
              />
              <span>4-player</span>
            </label>
            <label className="lobby-radio">
              <input
                type="radio"
                name="players"
                value="6"
                checked={lobby.config.players === 6}
                onChange={() => onPlayersChange(6)}
              />
              <span>6-player</span>
            </label>
          </fieldset>
          <fieldset className="lobby-fieldset" disabled={!isHost}>
            <legend>Jokers</legend>
            <label className="lobby-radio">
              <input
                type="radio"
                name="jokers"
                value="on"
                checked={lobby.config.jokers}
                onChange={() => onJokersChange(true)}
              />
              <span>On</span>
            </label>
            <label
              className="lobby-radio"
              data-disabled={lobby.config.players === 6 ? "true" : undefined}
              title={
                lobby.config.players === 6
                  ? "6-player is jokers-on only (v1.2)"
                  : undefined
              }
            >
              <input
                type="radio"
                name="jokers"
                value="off"
                checked={!lobby.config.jokers}
                onChange={() => onJokersChange(false)}
                // v1.2 Phase 13 (RULES §2 + §9 A3): 6-player is jokers-ON
                // only; the protocol's GameConfigSchema.refine() rejects
                // `{ players: 6, jokers: false }` at the wire layer, so the
                // UI disables the off-switch when Players is 6.
                disabled={lobby.config.players === 6}
              />
              <span>Off</span>
            </label>
          </fieldset>
          <fieldset className="lobby-fieldset" disabled={!isHost}>
            <legend>Teams</legend>
            <label className="lobby-radio">
              <input
                type="radio"
                name="teamMode"
                value="manual"
                checked={lobby.config.teamMode === "manual"}
                onChange={() => onTeamModeChange("manual")}
              />
              <span>Manual</span>
            </label>
            <label className="lobby-radio">
              <input
                type="radio"
                name="teamMode"
                value="random"
                checked={lobby.config.teamMode === "random"}
                onChange={() => onTeamModeChange("random")}
              />
              <span>Random (shuffled on Start)</span>
            </label>
          </fieldset>
        </div>
        <p className="lobby-config-note" aria-live="polite">
          {describeConfig(lobby.config.players, lobby.config.jokers)}
        </p>
      </section>

      <section className="lobby-seats-section" aria-labelledby="lobby-seats-title">
        <h2 id="lobby-seats-title" className="lobby-section-title">
          Seats ({lobby.seats.filter((s) => s.kind === "human").length}/{lobby.config.players} humans)
        </h2>
        <ol className="lobby-seats" role="list">
          {lobby.seats.map((seat) => (
            <LobbySeat
              key={seat.seatIndex}
              seat={seat}
              isYou={you !== null && seat.seatIndex === you}
              isHost={hostSeatIndex !== null && seat.seatIndex === hostSeatIndex}
              canSwap={false}
              // onSwap intentionally omitted — see file header for the
              // seat-swap decision. Wiring it up requires a server change
              // outside A3.3's ownership.
            />
          ))}
        </ol>
        {lobby.config.teamMode === "manual" ? (
          <p className="lobby-hint">
            Teams are decided by seat parity: A = {evenSeats(lobby.config.players)},
            B = {oddSeats(lobby.config.players)}. Switch to <em>Random</em> to shuffle on Start.
          </p>
        ) : (
          <p className="lobby-hint">
            <em>Random</em> teams: the server shuffles seating when the host presses Start.
          </p>
        )}
      </section>

      <footer className="lobby-footer">
        {isHost ? (
          <button
            type="button"
            className="lobby-start"
            onClick={onStart}
            disabled={!canStart}
            aria-label="Start the game"
          >
            Start game
          </button>
        ) : (
          <p className="lobby-waiting">
            Waiting for <strong>{hostName}</strong> to start the game…
          </p>
        )}
        <button type="button" className="lobby-leave" onClick={onBack}>
          Leave room
        </button>
      </footer>
    </main>
  );
}

function describeConfig(players: 4 | 6, jokers: boolean): string {
  if (players === 4 && jokers) return "4 players · 52 cards (2 of the four 2s removed at deal) + 2 jokers · 13 each · base bid 7 · 13 hands";
  if (players === 4 && !jokers) return "4 players · 52 cards · 13 each · base bid 7 · 13 hands";
  // v1.2 Phase 13: 6-player is jokers-ON only (RULES §2 + §9 A3). The
  // jokers-OFF radio is disabled in the UI; if the prop ever arrives as
  // false (e.g. cached lobby from a v1.1 client) we still render the
  // jokers-on description because that's what the server will run.
  return "6 players · 54 cards (52 + 2 jokers) · 9 each · base bid 5 · 9 hands";
}

function evenSeats(n: number): string {
  const out: string[] = [];
  for (let i = 0; i < n; i += 2) out.push(`#${i + 1}`);
  return out.join(", ");
}
function oddSeats(n: number): string {
  const out: string[] = [];
  for (let i = 1; i < n; i += 2) out.push(`#${i + 1}`);
  return out.join(", ");
}

